/**
 * usePostToShell — the embed side of the validated postMessage channel
 * (ADR-080 Amendment 2 §A2.3), shared by every chromeless /embed/* route
 * (the SOW sign-up embed and the sign-in embed) framed by the `www`
 * marketing-site modal shell (nexa-website sow-modal.js).
 *
 * Announces `hello` to each allowed parent origin (delivery only succeeds to
 * the true parent), accepts a validated `init` to learn the real parent
 * origin, then posts outbound messages to that origin exclusively. Before the
 * handshake completes nothing data-bearing is sent anywhere.
 *
 * The message schema (nexa-sow:*) is the one embed-shell contract — both
 * embeds speak it; the shell branches on which modal it has open, not on the
 * message namespace. Origin allowlist + shape validation live in
 * sow-embed-bridge (pure, unit-tested).
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  isAllowedParentOrigin,
  parseInboundMessage,
  type SowEmbedOutbound,
} from './sow-embed-bridge';

export function usePostToShell(): { post: (msg: SowEmbedOutbound) => void; framed: boolean } {
  const framed = typeof window !== 'undefined' && window.self !== window.top;
  const parentOriginRef = useRef<string | null>(null);

  // Dev-server special case: admit a localhost shell without widening the
  // production allowlist (the pure module stays exact-match on prod origins).
  const extraOrigins = useMemo<string[]>(
    () => (import.meta.env.DEV ? [`http://localhost:8788`, `http://localhost:3000`] : []),
    [],
  );

  useEffect(() => {
    if (!framed) return;
    function onMessage(e: MessageEvent) {
      // Origin check FIRST — messages from anything but the www shell are
      // dropped before the payload is even looked at (§A2.3).
      if (!isAllowedParentOrigin(e.origin, extraOrigins)) return;
      const msg = parseInboundMessage(e.data);
      if (!msg) return;
      if (msg.type === 'nexa-sow:init') parentOriginRef.current = e.origin;
    }
    window.addEventListener('message', onMessage);
    // Announce to each allowed origin; the browser delivers only to the one
    // that matches the actual parent, so a hostile framer receives nothing.
    for (const origin of ['https://nexanomics.com', 'https://www.nexanomics.com', ...extraOrigins]) {
      try {
        window.parent.postMessage({ type: 'nexa-sow:hello' }, origin);
      } catch {
        // targetOrigin mismatch — expected for all but the true parent
      }
    }
    return () => window.removeEventListener('message', onMessage);
  }, [framed, extraOrigins]);

  const post = useCallback((msg: SowEmbedOutbound) => {
    const target = parentOriginRef.current;
    if (!target) return; // handshake not complete — never post blind
    try {
      window.parent.postMessage(msg, target);
    } catch {
      // parent gone — nothing to do
    }
  }, []);

  return { post, framed };
}

/** Where an unframed (top-level) visit to an /embed/* route is sent — the
 *  embeds live on the marketing site as modals; they are not navigable
 *  pages (§A2.2). */
export const MARKETING_SITE_URL = 'https://nexanomics.com/';
